import { createSelector } from 'reselect';
import { orderBy } from 'natural-orderby';
import {
  getColumnFilters,
} from '../../utils';
import {
  getAssetManagerAssetSelection,
  getAssetManagerFilters,
  getAssetManagerSelectedAssetTypeId,
  getListViewColumns,
  getListViewDefaultSort,
} from '..';
import {
  getDataSourceKeyedByAssetId,
  getFilteredAssets,
  getFilteredAssetsContext,
} from '.';
import map from '../map';

// Returning a primitive prevents hover events (which only change hoveredAssetId)
// from invalidating the getColumnFiltersForAssetsTable cache.
const getShowSelectedOnly = createSelector(
  [getAssetManagerAssetSelection],
  ({ showSelectedOnly }) => showSelectedOnly,
);

export const getFilteredAssetsForListView = createSelector(
  [
    getAssetManagerFilters,
    getAssetManagerSelectedAssetTypeId,
    getFilteredAssetsContext,
  ],
  (
    assetManagerFilters,
    selectedAssetTypeId,
    filteredAssetsContext,
  ) => {
    const {
      searchTags,
      searchText,
      dateFilter,
      assetStatusFilter,
      observationTypeFilter,
      selectedLayerIds,
      selectedBandIds,
      tableFilter,
      openedLayerId,
      multiSearchModeFilter,
      fieldFilters,
    } = assetManagerFilters;

    return getFilteredAssets(filteredAssetsContext, {
      searchTags,
      searchText,
      dateFilter,
      selectedAssetTypeId,
      assetStatusFilter,
      observationTypeFilter,
      selectedLayerIds,
      selectedBandIds,
      tableFilter,
      openedLayerId,
      multiSearchModeFilter,
      fieldFilters,
    });
  },
);

export const getFilteredDataSourceForListView = createSelector(
  [
    getFilteredAssetsForListView,
    getDataSourceKeyedByAssetId,
    getListViewDefaultSort,
  ],
  (
    filteredAssetsForAssetsTable,
    dataSourceKeyedByAssetId,
    listViewDefaultSort,
  ) => {
    const unsorted = filteredAssetsForAssetsTable
      .map(a => dataSourceKeyedByAssetId.get(a.id)!);

    if (listViewDefaultSort) {
      const { key, direction } = listViewDefaultSort;
      return orderBy(unsorted, [a => a?.[key]], [direction!]);
    }

    return orderBy(unsorted, [a => a?.label], ['asc']);
  },
);

export const getSelectedAssetsDataSourceForListView = createSelector(
  [
    getAssetManagerAssetSelection,
    getDataSourceKeyedByAssetId,
  ],
  (
    assetManagerAssetSelection,
    dataSourceKeyedByAssetId,
  ) => {
    const { selectedAssets } = assetManagerAssetSelection;
    return selectedAssets.map(selected => dataSourceKeyedByAssetId.get(selected)!);
  },
);

export const getColumnFiltersForAssetsTable = createSelector(
  [
    map.timezone,
    getAssetManagerFilters,
    getShowSelectedOnly,
    getListViewColumns,
    getDataSourceKeyedByAssetId,
    getFilteredDataSourceForListView,
  ],
  (
    timezone,
    assetManagerFilters,
    showSelectedOnly,
    listViewColumns,
    listViewDataSource,
    filteredDataSource,
  ) => {
    const { tableSorter, tableFilter } = assetManagerFilters;
    return getColumnFilters(
      tableFilter,
      tableSorter,
      timezone,
      showSelectedOnly,
      listViewColumns,
      listViewDataSource,
      filteredDataSource,
    );
  },
);

export const getDataSourceForListView = createSelector(
  [
    getFilteredAssetsForListView,
    getDataSourceKeyedByAssetId,
  ],
  (
    filteredAssetsForAssetsTable,
    listViewDataSource,
  ) => filteredAssetsForAssetsTable.map(a => listViewDataSource.get(a.id)!),
);
